// The brand's raw color data, extracted from the preset so non-Vite consumers
// (the identity provider's managed-login branding derivation in the infra
// package) can import it from plain Node — index.mjs imports CSS, which only
// bundlers understand. index.mjs composes these into the PrimeVue preset
// unchanged; this file and index.mjs together are the whole brand.

// Mint accent ramp, anchored on --p-primary-color (#3ee0a4) at 400 — which is
// the shade dark mode promotes to `primary.color` — and --p-primary-contrast-color
// (#052b1a) at 950.
const PRIMARY_RAMP = {
  50: '#eefbf5',
  100: '#d0f5e4',
  200: '#a3ebcb',
  300: '#70deb0',
  400: '#49d29b',
  500: '#38d898',
  600: '#1eb87c',
  700: '#159363',
  800: '#0f7551',
  900: '#0a5a3f',
  950: '#063c2a',
};

// Slightly cool near-black neutrals matching the Midnight Mint surfaces.
const COLOR_SCHEMES = {
  // ── Light — the brand palette on Aura's slate paper. Cards are white on
  // a faint slate ground; the mint accent is promoted to primary.800 so it
  // stays legible both as text on white AND as a fill (with white ink).
  //
  // 800 RATHER THAN 500, AND THE DIFFERENCE IS A GATE. The design system's
  // light accent is the brand mint itself — `--accent: {primary.500}` with
  // `--fg-on-accent: {primary.950}` — and as a FILL that pairing is fine
  // (6.78:1). But PrimeVue has one `primary.color` and it serves the fill
  // AND the text: a text button, a link and a ghost button all read it
  // directly against the page. #38d898 as ink on white is 1.84:1, so
  // adopting the design system's accent literally would make every text
  // variant illegible in light and fail the catalog's axe gate.
  //
  // 800 (#0f7551) is 5.70:1 and works as both. If the bright-mint fill in
  // the comps is wanted, it belongs as a Button-specific override in the
  // `components` block below, not as `primary.color`.
  light: {
    // Aura's own slate ramp, which the design system explicitly keeps
    // ("surface.* → Aura slate (kept — cool, modern, reads as
    // auction-house)"). This replaces the near-neutral ramp that was here,
    // which was the same idea drawn by hand.
    surface: {
      0: '#ffffff',
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
      950: '#020617',
    },
    primary: {
      color: '{primary.800}',
      contrastColor: '#ffffff',
      hoverColor: '{primary.900}',
      activeColor: '{primary.950}',
    },
    // Mint-tinted selection highlight, faint on paper.
    highlight: {
      background: 'color-mix(in srgb, {primary.500}, transparent 86%)',
      focusBackground: 'color-mix(in srgb, {primary.500}, transparent 78%)',
      color: '{surface.800}',
      focusColor: '{surface.900}',
    },
    text: {
      color: '{surface.700}',
      hoverColor: '{surface.800}',
      // 600 RATHER THAN 500, AND THE REASON IS THAT MUTED TEXT IS NOT ONLY
      // EVER ON WHITE. #6b7280 clears AA on `content.background` at 4.83:1
      // and then fails on the step above it — 4.16:1 on
      // `content.hoverBackground` (#eceef2), which is the ground a sheet, a
      // receipt line and every hovered row actually sit on. A muted token
      // that is only correct on the lightest surface in the scheme is one
      // every elevated surface has to override, and per-component overrides
      // of a semantic token are how a palette stops being a palette.
      // #4b5059 is 8.11:1 on white and 6.98:1 on the elevated step, so the
      // one value is right everywhere the token is read.
      mutedColor: '{surface.600}',
      // AND THE HOVER STEP MOVES WITH IT, because the pair has to stay a
      // pair. Muted was 500 and hover-muted 600; leaving hover at 600 once
      // muted arrived there would have collapsed the two into one value and
      // silently retired a step around twenty rules in the mobile portal
      // read — as a SECOND MUTED TIER rather than as a hover, which is what
      // most of them use it for.
      //
      // 700 is the same value as `text.color`, deliberately and with the
      // design call behind it: the tier this token really names is "muted,
      // but not the quiet one", and full text color is what that has always
      // resolved to by eye. #343840 is 11.76:1 on white and 10.12:1 on the
      // elevated step.
      hoverMutedColor: '{surface.700}',
    },
    // White cards; the elevated step reads as a subtle inset gray.
    content: {
      background: '{surface.0}',
      hoverBackground: '{surface.100}',
      borderColor: '{surface.200}',
    },
    formField: {
      background: '{surface.0}',
      filledBackground: '{surface.50}',
      borderColor: '{surface.300}',
      focusBorderColor: '{primary.color}',
    },
  },
  dark: {
    // The design system's Carbon ramp — pure charcoal grays with no hue,
    // which is the point of it: the ramp this replaces carried a slight
    // blue cast (#14161a rather than #141414), and the design system calls
    // that out as the thing dark mode should lose.
    //
    // The three the rest of the file depends on land where they did:
    // 950 is the page ground, 900 the card, 800 the hover step.
    surface: {
      0: '#ffffff',
      50: '#f6f6f6',
      100: '#ebebeb',
      200: '#d6d6d6',
      300: '#a8a8a8',
      400: '#7a7a7a',
      500: '#545454',
      600: '#3d3d3d',
      700: '#2e2e2e',
      800: '#1f1f1f', // --p-content-hover-background
      900: '#141414', // --p-content-background
      950: '#0a0a0a', // --surface-ground
    },
    primary: {
      color: '{primary.400}',
      contrastColor: '{primary.950}',
      hoverColor: '{primary.300}',
      activeColor: '{primary.200}',
    },
    // Mint-tinted selection highlight (replaces Aura's default).
    highlight: {
      background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
      focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
      color: '#f4f4f4',
      focusColor: '#f4f4f4',
    },
    // Ink from the design system's dark block: `--fg-primary: #f4f4f4`.
    // The muted pair stays translucent white rather than taking the
    // design system's `--fg-secondary: {carbon.300}` literally — a flat
    // #a8a8a8 muted is 7.75:1 on the card but the same gray wherever it
    // lands, while these track whatever surface is underneath. Their
    // `--fg-tertiary: {carbon.500}` (#545454) is 2.43:1 on the card and is
    // deliberately not adopted anywhere ink is read.
    text: {
      color: '#f4f4f4', // --p-text-color
      hoverColor: '#f4f4f4',
      mutedColor: 'rgba(235, 235, 245, 0.50)', // --p-text-muted-color
      hoverMutedColor: 'rgba(235, 235, 245, 0.62)', // --p-text-hover-muted-color
    },
    // Cards / panels / overlays read from surface.900 (= --p-content-background).
    content: {
      background: '{surface.900}',
      hoverBackground: '{surface.800}',
      borderColor: 'rgba(255, 255, 255, 0.08)', // --p-content-border-color
    },
    // Inputs sit on the elevated surface, not the deepest ground.
    formField: {
      background: '{surface.800}',
      filledBackground: '{surface.800}',
      borderColor: 'rgba(255, 255, 255, 0.14)', // --p-form-field-border-color
      focusBorderColor: '{primary.color}',
    },
  },
};

// Only the radii that exceed PrimeVue's scale, which caps at 12px
// (`--p-border-radius-xl`). The 12px button radius is that token exactly,
// so it is read from PrimeVue directly rather than restated here.
const RADIUS = {
  card: '18px',
  row: '{borderRadius.xl}',
};

export { PRIMARY_RAMP, COLOR_SCHEMES, RADIUS };
